import {
  AdInsertionType,
  AdMarker,
  AdVideoVariant,
  API_ERROR,
  AWPError,
  BufferSeekingPayload,
  convertVodIAdBreaksToAdMarkers,
  CoreEvents,
  CoreEventsMap,
  ERROR_CATEGORY,
  ErrorLevel,
  EventEmitter,
  getRemoteConfigValue,
  IAd,
  IAdBreak,
  TimePayload,
} from "@tv4/avod-web-player-common";

import { MediaID3Payload } from "../media_engine/utils/mediaConstants";
import {
  TSSAIAdEngineParams,
  TSSAISeekHandler,
} from "../playback/SSAIPlayback";
import { hasAdImmunity, setAdImmunity } from "../utils/adImmunity";
import {
  Ad,
  AdBreak,
  createPrismSession,
  PrismSession,
} from "./prism-sdk/prism";

const AD_VIDEO_VARIANT = {
  bumper: AdVideoVariant.VIGNETTE,
  sponsor: AdVideoVariant.SPONSOR,
  trailer: AdVideoVariant.TRAILER,
};

function convertAd(ad: Ad): IAd {
  const variant =
    AD_VIDEO_VARIANT[ad.adType.toLowerCase()] ?? AdVideoVariant.NORMAL;
  return {
    id: ad.universalAdId,
    universalAdId: ad.universalAdId,
    title: ad.title,
    variant,
    creative: {
      id: ad.campaignId,
      adId: ad.campaignId,
      type: ad.adType,
      duration: ad.end - ad.start,
      mediafiles: [],
      trackingEvents: {
        clickThrough: ad.clickTracking,
      },
      clickThroughUrlTemplate: ad.clickThrough
        ? {
            id: ad.campaignId,
            url: ad.clickThrough,
          }
        : undefined,
    },
  };
}

export class PrismAdEngine extends EventEmitter<CoreEventsMap> {
  public readonly name = "PrismAdEngine";
  public readonly insertionType = AdInsertionType.ServerSide;

  private assetId: string;
  private seekHandler: TSSAISeekHandler;

  private session?: PrismSession;
  private sessionError?: Error;
  private sessionPromise: Promise<PrismSession>;

  private adsRequired = true;
  private started = false;
  private redirecting = false;
  private destroyed = false;

  private lastStreamPosition: number = 0;
  private redirectAfterBreakEndPosition?: number;

  constructor(options: TSSAIAdEngineParams) {
    super();

    if (!options.media.accessUrl) {
      throw new Error("[PrismAdEngine] No access URL provided");
    }

    this.assetId = options.metadata.asset.id;
    this.seekHandler = options.seekHandler;
    this.adsRequired = !options.capabilities.skip_ads;

    this.sessionPromise = createPrismSession({
      sessionUrl: options.media.accessUrl,
      callbacks: {
        onAdBreakStart: (adBreak) => {
          this.emit(CoreEvents.BREAK_START, {
            adBreak: this.convertAdBreak(adBreak),
          });
        },
        onAdBreakEnd: () => {
          setAdImmunity(this.assetId);

          this.emit(CoreEvents.BREAK_END, {});
          if (this.redirectAfterBreakEndPosition !== undefined) {
            this.seekHandler(this.redirectAfterBreakEndPosition);
            this.redirectAfterBreakEndPosition = undefined;
          }
        },
        onAdStart: (advert) => {
          const ad = convertAd(advert);
          this.emit(CoreEvents.AD_LOADING, {
            ad,
          });
          this.emit(CoreEvents.AD_MEDIA_LOADING, {
            src: "",
          });
          this.emit(CoreEvents.AD_LOADED, {
            ad,
          });

          this.emit(CoreEvents.AD_START, {
            ad,
          });
        },
        onAdEnd: () => {
          this.emit(CoreEvents.AD_ENDED, undefined);
        },
      },
    }).catch((e) => {
      throw new AWPError({
        context: "core",
        message: `[PrismAdEngine] session initialization failed`,
        category: ERROR_CATEGORY.API,
        raw: e,
        code: API_ERROR.PRISM,
        errorLevel: ErrorLevel.PLAYER,
      });
    });
  }

  private convertAdBreak(adBreak: AdBreak): IAdBreak {
    if (!this.session) {
      throw new Error("[PrismAdEngine] No session available");
    }
    return {
      insertionType: AdInsertionType.ServerSide,
      breakType: adBreak.start === 0 ? "preroll" : "midroll",
      timeOffset: this.session?.getContentPositionForStreamPosition(
        adBreak.start
      ),
      ads: adBreak.ads.map(convertAd),
    };
  }

  private redirectPlayerPosition(streamPosition: number) {
    this.redirecting = true;
    this.seekHandler(streamPosition);
  }

  public async createSession(): Promise<string | null> {
    this.session = await this.sessionPromise;
    if (this.destroyed) {
      return null;
    }
    return this.session.manifestUrl.toString();
  }

  private getAdBreaks(): IAdBreak[] {
    return this.session?.adBreaks.map(this.convertAdBreak.bind(this)) ?? [];
  }

  public getVodAdMarkers(): AdMarker[] {
    return convertVodIAdBreaksToAdMarkers(this.getAdBreaks());
  }
  public markAdBreaksBeforeStartTimeAsWatched(streamPosition: number): void {
    if (this.session) {
      this.session.deactivateAdBreaksBefore(streamPosition);
    }
  }

  public hasAdImmunity(): boolean {
    if (!this.adsRequired) return true;
    return hasAdImmunity(
      this.assetId,
      getRemoteConfigValue("AD_IMMUNITY_DURATION")
    );
  }

  public isInAdBreak(): boolean {
    return this.session?.isInAdBreak ?? false;
  }

  public getContentPositionForPlayheadPosition(
    playheadPosition: number
  ): number {
    if (!this.session) {
      return playheadPosition;
    }
    return this.session.getContentPositionForStreamPosition(playheadPosition);
  }

  public getPlayheadPositionForContentPosition(
    contentPosition: number
  ): number {
    if (!this.session) {
      return contentPosition;
    }
    return this.session.getStreamPositionForContentPosition(contentPosition);
  }

  private getAdTimePayload(
    streamTimePayload: TimePayload,
    advert: Ad
  ): TimePayload {
    return {
      ...streamTimePayload,
      currentTime: streamTimePayload.currentTime - advert.start,
      duration: advert.duration,
      isInAdBreak: true,
    };
  }

  private handleAnyTimePayload(payload: TimePayload) {
    this.session?.handleStreamPositionUpdate(payload.currentTime);
  }

  public handlePlaying(payload: TimePayload): boolean {
    this.handleAnyTimePayload(payload);

    const wasStarted = this.started;
    this.started = true;

    const advert = this.session?.activeAd;
    if (advert) {
      if (wasStarted) {
        this.session?.handleResume();
        this.emit(CoreEvents.AD_RESUME, {
          ...this.getAdTimePayload(payload, advert),
          ad: convertAd(advert),
        });
      }
      return true;
    }
    return false;
  }

  public handlePaused(payload: TimePayload): boolean {
    this.handleAnyTimePayload(payload);
    const advert = this.session?.activeAd;
    if (advert) {
      this.session?.handlePause();
      this.emit(CoreEvents.AD_PAUSED, this.getAdTimePayload(payload, advert));
      return true;
    }
    return false;
  }

  public handleTimeUpdate(payload: TimePayload): boolean {
    this.lastStreamPosition = payload.currentTime;

    const upcomingAdBreak = !this.isInAdBreak()
      ? this.session?.getAdBreakForStreamPosition(payload.currentTime)
      : undefined;

    if (upcomingAdBreak && (upcomingAdBreak.watched || this.hasAdImmunity())) {
      this.redirectPlayerPosition(upcomingAdBreak.end);
      return true;
    }

    this.handleAnyTimePayload(payload);
    const advert = this.session?.activeAd;
    if (advert) {
      this.emit(
        CoreEvents.AD_TIME_UPDATED,
        this.getAdTimePayload(payload, advert)
      );
      // Block the content time update for VOD only
      return true;
    }
    return false;
  }

  public handleSeeking(_: BufferSeekingPayload): boolean {
    if (this.redirecting) {
      return true;
    }
    if (this.isInAdBreak()) {
      // handleSeeked will restore the position thus eliminating this
      // seeking event so we make sure to ignore it by returning true
      // to mark it as handled.
      return true;
    }
    return false;
  }

  public handleSeeked(payload: BufferSeekingPayload): boolean {
    if (this.redirecting) {
      this.redirecting = false;
      return true;
    }

    const from = this.lastStreamPosition;
    const to = payload.currentTime;

    if (
      this.hasAdImmunity() ||
      // seeking backwards is always allowed
      to < from
    ) {
      return false;
    }

    const adBreaks = this.session?.adBreaks ?? [];
    let seekedOverAdBreak: AdBreak | undefined;
    for (let i = adBreaks.length - 1; i >= 0; i--) {
      const adBreak = adBreaks[i];
      if (adBreak.start > from && adBreak.start < to) {
        seekedOverAdBreak = adBreak;
        break;
      }
    }
    if (seekedOverAdBreak && !seekedOverAdBreak.watched) {
      this.redirectAfterBreakEndPosition = to;
      this.redirectPlayerPosition(seekedOverAdBreak.start);
    }

    if (this.isInAdBreak()) {
      return true;
    }
    return false;
  }

  public handleBuffering(payload: BufferSeekingPayload): boolean {
    const advert = this.session?.activeAd;
    if (advert) {
      this.emit(CoreEvents.AD_BUFFERING, {
        ...payload,
        ...this.getAdTimePayload(payload, advert),
      });
      return true;
    }
    return false;
  }

  public handleBuffered(payload: BufferSeekingPayload): boolean {
    const advert = this.session?.activeAd;
    if (advert) {
      this.emit(CoreEvents.AD_BUFFERED, {
        ...payload,
        ...this.getAdTimePayload(payload, advert),
      });
      return true;
    }
    return false;
  }
  public handleEnded(payload: TimePayload): boolean {
    this.handleAnyTimePayload(payload);
    return false;
  }
  public handleID3(_: MediaID3Payload): void {
    // no-op
  }

  public override destroy(): void {
    super.destroy();
    this.destroyed = true;
  }
}
